import { z } from "@/zod";
import { projectDependsOn, ulid } from "@/schemas/commonFields";
import { emptyStringToNull } from "@/lib/utils/zod";

export const PROJECT_STATUS_CREATED = "created";
export const PROJECT_STATUS_APPROVAL_REQUESTED = "approval_requested";
export const PROJECT_STATUS_APPROVED = "approved";
export const PROJECT_STATUS_REJECTED = "rejected";
export const PROJECT_STATUS_CLOSED = "closed";
export const PROJECT_STATUS_DELETED = "deleted";

export const PROJECT_STATUS = [
  PROJECT_STATUS_CREATED,
  PROJECT_STATUS_APPROVAL_REQUESTED,
  PROJECT_STATUS_APPROVED,
  PROJECT_STATUS_REJECTED,
  PROJECT_STATUS_CLOSED,
  PROJECT_STATUS_DELETED,
] as const;

export const LOCKED_STATUSES = [PROJECT_STATUS_CLOSED, PROJECT_STATUS_DELETED];

export const projectComment = z.object({
  id: z.string().length(26),
  project_id: z.string().length(26),
  comment: z.string(),
  created_at: z.string(),
  created_by: z.string().length(26),
  created_by_name: z.string(),
  created_by_role: z.string().optional(),
  created_by_role_name: z.string().optional(),
});

export const project = z.object({
  id: ulid,
  ref_number: z.number().nonnegative().optional(),
  crs_number: z.string().nullable(),
  name: z.string().min(1).max(100),
  client_id: ulid,
  client: z.string(),
  project_type_id: ulid,
  project_type: z.string(),
  services_and_goods_id: ulid.optional(),
  services_and_goods: z.string().optional(),
  services_and_goods_code: z.string().optional(),
  services_and_goods_invoice_description: z.string().optional(),
  invoicing_company_id: ulid,
  invoicing_company: z.string(),
  start_date: z.string().date(),
  end_date: z.string().date(),
  primary_manager: z.string(),
  primary_manager_id: ulid,
  primary_manager_active: z.boolean(),
  status: z.enum(PROJECT_STATUS).optional(),
  project_depends_on: projectDependsOn,
  geolocalization_enabled: z.boolean().default(false),
  payroll_type_id: emptyStringToNull(ulid.optional().nullable()),
  payroll_type: z.string().optional(),
  contractor_card_required: z.boolean().optional(),
  contractor_visits_planned_duration_visible: z.boolean().optional(),
  contractor_visits_editable_by_pr: z.boolean().optional(),
  contractor_visits_hours_editable_by_contractor: z.boolean().optional(),
  contractor_visits_confirmable_by_contractor: z.boolean().optional(),
  can_request_approval: z.boolean().optional(),
  can_be_approved: z.boolean().optional(),
  can_be_rejected: z.boolean().optional(),
  can_be_closed: z.boolean().optional(),
  can_be_deleted: z.boolean().optional(),
  can_be_reopened: z.boolean().optional(),
  can_be_restored: z.boolean().optional(),
  is_editable: z.boolean().optional(),
  last_confirmed_period: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .nullable()
    .optional(),
  show_period_confirmation_alert: z.boolean().optional(),
  comments: z.array(projectComment).optional(),
});

export const readProject = project.readonly();

export const basicListingProject = project
  .pick({
    id: true,
    name: true,
    ref_number: true,
    project_type: true,
    project_type_id: true,
    client: true,
    client_id: true,
    start_date: true,
    end_date: true,
    primary_manager: true,
    primary_manager_id: true,
    last_confirmed_period: true,
  })
  .readonly();

export const fullListingProject = project
  .omit({
    can_request_approval: true,
    can_be_approved: true,
    can_be_rejected: true,
    can_be_closed: true,
    can_be_deleted: true,
    can_be_reopened: true,
    can_be_restored: true,
    is_editable: true,
    comments: true,
    primary_manager_active: true,
    show_period_confirmation_alert: true,
  })
  .readonly();

export const listingProject = fullListingProject;

export const writeProject = project.omit({
  id: true,
  project_type: true,
  invoicing_company: true,
  client: true,
  status: true,
  ref_number: true,
  project_depends_on: true,
  primary_manager: true,
  primary_manager_active: true,
  services_and_goods_code: true,
  payroll_type: true,
  last_confirmed_period: true,
  show_period_confirmation_alert: true,
});

export const createProject = writeProject;

export const updateRoleBasedProject = writeProject.pick({
  name: true,
  crs_number: true,
  invoicing_company_id: true,
  primary_manager_id: true,
  services_and_goods_id: true,
  geolocalization_enabled: true,
  payroll_type_id: true,
  contractor_card_required: true,
  contractor_visits_planned_duration_visible: true,
  contractor_visits_editable_by_pr: true,
  contractor_visits_hours_editable_by_contractor: true,
  contractor_visits_confirmable_by_contractor: true,
});

export const updateDepartmentBasedProject = writeProject.pick({
  name: true,
  crs_number: true,
  invoicing_company_id: true,
  primary_manager_id: true,
  services_and_goods_id: true,
  geolocalization_enabled: true,
  contractor_card_required: true,
  contractor_visits_planned_duration_visible: true,
  contractor_visits_editable_by_pr: true,
  contractor_visits_hours_editable_by_contractor: true,
  contractor_visits_confirmable_by_contractor: true,
});
